import React, { createContext, useContext, useState, useEffect } from "react";
import { apiRequest } from "../utils/api";

const FinancialContext = createContext();

export const FinancialProvider = ({ children }) => {
  const currentYear = new Date().getFullYear();

  const [activos, setActivos] = useState([
    {
      nArticulo: "A123",
      nombre: "ARTÍCULO A",
      fabricante: "Wells & Co.",
      descripcion: "Descripción del Artículo A",
      costoPorArticulo: 12.00,
      cantidadExistencias: 45,
      valorActivo: 540.00,
      nivelReordenacion: 50,
      diasPorReordenacion: 14,
      cantidadReordenacion: 100,
      descartado: "sí",
    },
    {
      nArticulo: "C123",
      nombre: "ARTÍCULO C",
      fabricante: "Cole",
      descripcion: "Descripción del Artículo C",
      costoPorArticulo: 30.00,
      cantidadExistencias: 50,
      valorActivo: 1500.00,
      nivelReordenacion: 50,
      diasPorReordenacion: 2,
      cantidadReordenacion: 50,
      descartado: "no",
    },
    {
      nArticulo: "D123",
      nombre: "ARTÍCULO D",
      fabricante: "Sandling Co.",
      descripcion: "Descripción del Artículo D",
      costoPorArticulo: 10.00,
      cantidadExistencias: 20,
      valorActivo: 200.00,
      nivelReordenacion: 50,
      diasPorReordenacion: 14,
      cantidadReordenacion: 100,
      descartado: "sí",
    },
    {
      nArticulo: "F123",
      nombre: "ARTÍCULO F",
      fabricante: "Cole",
      descripcion: "Descripción del Artículo F",
      costoPorArticulo: 30.00,
      cantidadExistencias: 100,
      valorActivo: 3000.00,
      nivelReordenacion: 50,
      diasPorReordenacion: 2,
      cantidadReordenacion: 20,
      descartado: "sí",
    },
    {
      nArticulo: "H123",
      nombre: "ARTÍCULO H",
      fabricante: "Cole",
      descripcion: "Descripción del Artículo H",
      costoPorArticulo: 20.00,
      cantidadExistencias: 20,
      valorActivo: 400.00,
      nivelReordenacion: 50,
      diasPorReordenacion: 14,
      cantidadReordenacion: 50,
      descartado: "sí",
    },
  ]);

  const [patrimonioData, setPatrimonioData] = useState(() => {
    const savedPatrimonio = localStorage.getItem("patrimonioData");
    return savedPatrimonio ? JSON.parse(savedPatrimonio) : {};
  });

  useEffect(() => {
    localStorage.setItem("patrimonioData", JSON.stringify(patrimonioData));
  }, [patrimonioData]);

  const pasivos = Object.values(patrimonioData).flatMap(yearData => 
    yearData.pasivos.corrientes.concat(yearData.pasivos.noCorrientes)
  );
  const setPasivos = (newPasivos) => {
    setPatrimonioData(prev => {
      const updatedData = { ...prev };
      const year = currentYear;
      updatedData[year] = {
        ...updatedData[year],
        pasivos: {
          corrientes: newPasivos.filter(p => p.tipoPasivo === "corriente"),
          noCorrientes: newPasivos.filter(p => p.tipoPasivo === "no_corriente")
        }
      };
      return updatedData;
    });
  };

  const [costosData, setCostosData] = useState(() => {
    const savedCostos = localStorage.getItem("costosData");
    return savedCostos ? JSON.parse(savedCostos) : {
      directCosts: [
        { id: 1, insumo: 'arcilla', cantidad: 1.0, costoUnitario: 0.80, costoTotal: 0.80 },
        { id: 2, insumo: 'esmalte', cantidad: 1.0, costoUnitario: 0.15, costoTotal: 0.15 }
      ],
      laborCosts: [
        { id: 1, empleado: 'artista', horas: 3, costoPorHora: 3.00, costoTotal: 9.00 }
      ],
      indirectCosts: [
        { id: 1, concepto: 'Energía', costoMensual: 0.50 },
        { id: 2, concepto: 'Agua', costoMensual: 0.10 },
        { id: 3, concepto: 'Alquiler', costoMensual: 1.59 },
        { id: 4, concepto: 'Empaque', costoMensual: 1.00 }
      ],
      production: { costoTotal: 13.14, unidades: 1, costoUnitario: 13.14 }
    };
  });

  useEffect(() => {
    localStorage.setItem("costosData", JSON.stringify(costosData));
  }, [costosData]);

  const savePatrimonio = async (patrimonioData) => {
    try {
      const response = await apiRequest('/patrimonio', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patrimonioData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error al guardar el patrimonio:', response.status, errorData);
        throw new Error(`Error ${response.status}: ${errorData.message || 'Error al guardar los datos'}`);
      }

      const savedData = await response.json();
      return savedData;
    } catch (error) {
      console.error('Error en savePatrimonio:', error);
      throw error;
    }
  };

  useEffect(() => {
    const savePatrimonioData = async () => {
      if (patrimonioData && Object.keys(patrimonioData).length > 0) {
        try {
          await savePatrimonio(patrimonioData);
        } catch (error) {
          console.error('No se pudo guardar automáticamente el patrimonio:', error);
        }
      }
    };

    const timer = setTimeout(() => {
      savePatrimonioData();
    }, 1000);

    return () => clearTimeout(timer);
  }, [patrimonioData]);

  return (
    <FinancialContext.Provider value={{ 
      activos, 
      setActivos, 
      pasivos, 
      setPasivos, 
      patrimonioData, 
      setPatrimonioData, 
      costosData, 
      setCostosData,
      savePatrimonio
    }}>
      {children}
    </FinancialContext.Provider>
  );
};

export const useFinancial = () => {
  const context = useContext(FinancialContext);
  if (!context) {
    throw new Error("useFinancial must be used within a FinancialProvider");
  }
  return context;
};
